import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, ArrowLeft } from "lucide-react";
import { toast as showToast } from "sonner";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { useAuth } from "@/_core/hooks/useAuth";

export default function SelfServiceLogin() {
  useTenantTheme(); // Apply tenant theme
  const [email, setEmail] = useState("");
  const [, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();

  // Redirect logged-in users directly to self-service portal
  useEffect(() => {
    if (!authLoading && user) {
      setLocation("/self-service");
    }
  }, [user, authLoading, setLocation]);

  const requestMagicLinkMutation = trpc.selfService.requestMagicLink.useMutation({
    onSuccess: (data) => {
      showToast.success("Magic Link versendet", {
        description: "Bitte überprüfen Sie Ihre E-Mails. Der Link ist 24 Stunden gültig.",
      });
      // For development, show the token in console
      if (data.token) {
        console.log("Magic Link Token (dev only):", data.token);
        console.log("Magic Link URL:", `${window.location.origin}/self-service?token=${data.token}`);
      }
    },
    onError: (error) => {
      showToast.error("Fehler", {
        description: error.message || "Es ist ein Fehler aufgetreten",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      showToast.error("Fehler", {
        description: "Bitte geben Sie Ihre E-Mail-Adresse ein",
      });
      return;
    }
    requestMagicLinkMutation.mutate({ email });
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück zur Website
            </Button>
          </div>
          <CardTitle className="text-2xl font-bold">Self-Service Portal</CardTitle>
          <CardDescription>
            Geben Sie Ihre E-Mail-Adresse ein, um einen Zugangslink zu erhalten
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail-Adresse</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="ihre@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Wir senden Ihnen einen Link, mit dem Sie Ihre Daten verwalten können
              </p>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={requestMagicLinkMutation.isPending}
            >
              {requestMagicLinkMutation.isPending ? "Wird gesendet..." : "Magic Link anfordern"}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-muted rounded-lg">
            <h3 className="font-semibold mb-2">Was kann ich im Self-Service Portal?</h3>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>• Ihre persönlichen Daten aktualisieren</li>
              <li>• SMS-Benachrichtigungen aktivieren/deaktivieren</li>
              <li>• Ihre gebuchten Kurse einsehen</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
